import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { useState } from "react";
import {
  Truck,
  PackageCheck,
  Wrench,
  CreditCard,
  Leaf,
  Unlock,
  Activity,
  CheckCircle2,
} from "lucide-react";
import SiteVisitForm from "@/components/SiteVisitForm";
import Accordion from "@/components/Accordion";

import { Helmet } from "react-helmet-async";

const REGIONS = {
  AU: {
    label: "Country-wide",
    powerCost: "approx. $100/year",
    machineWidth: "1056mm W",
    routeBase: "/free-vending-machines-for-businesses",
  },
  UK: {
    label: "United Kingdom",
    powerCost: "approx. £60/year",
    machineWidth: "1056mm W",
    routeBase: "/free-vending-machines-for-businesses-uk",
  },
  US: {
    label: "United States",
    powerCost: "approx. $110/year",
    machineWidth: "41 in (1056mm) W",
    routeBase: "/free-vending-machines-for-businesses-usa",
  },
};

const FAQS = [
  { q: "Is it really free?", a: "Yes — for qualifying sites we cover delivery, install, stocking and ongoing servicing. We earn from product sales, not from charging your business." },
  { q: "What sites qualify?", a: "Workplaces with 30+ staff or steady foot traffic. Below that, foot traffic and site type can still qualify — submit the form and we'll confirm." },
  { q: "Can we choose what goes in it?", a: "Yes. You pick the product mix. We'll tune it over time based on what actually moves." },
  { q: "What if it doesn't get used?", a: "We'll work with you on placement and mix first. If usage stays low, we collect the machine — no penalties, no lock-in." },
];

export default function FreeVendingMachines({ region = "AU", testId = "free-vending-page-au" }) {
  const cfg = REGIONS[region];
  return (
    <div data-testid={testId} className="text-white">
      <Seo />
      <HeroSection cfg={cfg} />
      <ThreeFeaturePillsRow />
      <WhatsIncludedSection />
      <QualifyingBanner />
      <ProcessSection />
      <MachineAndCategoriesSection cfg={cfg} />
      <FAQSection />
      <FormSection />
    </div>
  );
}

function HeroSection({ cfg }) {
  const badges = [
    "No upfront cost (qualifying sites)",
    "Custom snacks & drinks",
    "No lock-in contracts",
  ];
  const pills = ["Free install", "Fully serviced", "Tap & Go", "Healthy options"];
  return (
    <section className="relative overflow-hidden border-b border-white/5">
      <div className="absolute inset-0 bg-radial-fade" />
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="relative mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center rounded-full border border-[#FE7200]/30 bg-[#FE7200]/10 px-3 py-1 text-xs font-semibold tracking-wide text-[#FE7200]">
            Anytime Vending Machines · {cfg.label}
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-balance">
            <span className="text-white">Free Vending Machines</span>
            <span className="block text-[#FE7200]">For Businesses</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">
            If your workplace has <b className="text-white">30+ staff</b> (or steady foot traffic), you may qualify for a <b className="text-white">brand new vending machine installed free</b> — fully stocked, monitored and serviced by us.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="#book-form"
              data-testid="free-hero-book"
              className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
            >
              Book Free Site Visit
            </a>
            <a
              href="#how-it-works"
              data-testid="free-hero-how"
              className="inline-flex items-center justify-center rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-bold uppercase tracking-wider text-white hover:bg-white/10 transition-colors"
            >
              How It Works
            </a>
          </div>
          <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-300">
            {badges.map((b) => (
              <li key={b} className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#FE7200]" /> {b}
              </li>
            ))}
          </ul>
          <div className="mt-5 flex flex-wrap gap-2">
            {pills.map((p) => (
              <span key={p} className="inline-flex items-center rounded-full border border-white/10 bg-[#121212] px-3 py-1 text-xs text-zinc-300">
                {p}
              </span>
            ))}
          </div>
          <div className="mt-6 rounded-xl border border-[#FE7200]/40 bg-[#FE7200]/10 p-4 text-sm text-[#FFB870]">
            <b className="text-white">Best fit:</b> workplaces with <b className="text-white">30–80+ staff</b>. If you're below that, still submit — foot traffic and site type can also qualify.
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7 shadow-[0_30px_60px_-20px_rgba(254,114,0,0.2)]">
            <h3 className="font-display text-xl font-bold text-white">What you get</h3>
            <p className="mt-2 text-sm text-zinc-300">
              A modern machine that looks professional and stays stocked — without you managing anything.
            </p>
            <ul className="mt-5 space-y-3 text-sm">
              {[
                [<b className="text-white">Free install</b>, " for qualifying sites"],
                [<b className="text-white">Tap & Go</b>, " + cash/coin"],
                [<b className="text-white">Stocked & serviced</b>, " by us"],
                [<b className="text-white">No lock-in</b>, " agreements"],
              ].map((row, i) => (
                <li key={i} className="flex items-start gap-3 text-zinc-300">
                  <CheckCircle2 className="h-4 w-4 text-[#FE7200] mt-0.5" />
                  <div>{row[0]}{row[1]}</div>
                </li>
              ))}
            </ul>
            <a
              href="#book-form"
              data-testid="free-sidebar-book"
              className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#FE7200] px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] text-black hover:bg-[#FF8C2E] transition-colors"
            >
              Book Free Site Visit
            </a>
          </div>
        </aside>
      </div>
    </section>
  );
}

function ThreeFeaturePillsRow() {
  const items = [
    { icon: Truck, label: "Free delivery & installation" },
    { icon: Wrench, label: "Regular servicing & restocking" },
    { icon: Activity, label: "Back-to-base stock monitoring" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 -mt-6 md:-mt-8" data-testid="free-feature-pills">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {items.map((it, i) => (
          <div
            key={it.label}
            data-testid={`free-feature-pill-${i}`}
            className="rounded-2xl border border-white/10 bg-[#0f0f10] px-5 py-4 flex items-center gap-3 text-sm text-zinc-200 hover:border-[#FE7200]/40 transition-colors"
          >
            <it.icon className="h-5 w-5 text-[#FE7200]" />
            <span className="font-semibold">{it.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function WhatsIncludedSection() {
  const items = [
    { icon: Truck, title: "Free delivery & install", text: "We bring the machine to your site and set it up in the best location." },
    { icon: PackageCheck, title: "Fully stocked", text: "Choose your product mix. We supply snacks, drinks, and food options." },
    { icon: Wrench, title: "Serviced & maintained", text: "Regular restocking, cleaning, and prompt maintenance if needed." },
    { icon: CreditCard, title: "Modern payments", text: "Tap & go / PayPass plus cash and coin options for everyone." },
    { icon: Leaf, title: "Healthy options", text: "Want fewer treats? We can stock water, kombucha, nut bars, and more." },
    { icon: Unlock, title: "No lock-in", text: "If it's not being used, we can collect it. No awkward contracts." },
  ];
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-white/5 mt-6" data-testid="free-whats-included">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
        What's included <span className="text-zinc-400">(at no upfront cost)</span>
      </h2>
      <p className="mt-3 text-zinc-300 max-w-3xl">
        Everything you need to run a hassle-free vending machine on-site — without the headaches.
      </p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((it, i) => (
          <div
            key={it.title}
            data-testid={`free-included-card-${i}`}
            className="group rounded-2xl border border-white/10 bg-[#121212] p-6 hover:border-[#FE7200]/40 hover:-translate-y-1 transition-all"
          >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#FE7200]/15 border border-[#FE7200]/30 text-[#FE7200]">
              <it.icon className="h-5 w-5" />
            </div>
            <h3 className="mt-4 font-display text-lg font-bold text-white">{it.title}</h3>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{it.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function QualifyingBanner() {
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12" data-testid="free-qualifying-banner">
      <div className="rounded-2xl border border-[#FE7200]/30 bg-gradient-to-r from-[#FE7200]/15 to-transparent p-6 md:p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="font-display text-xl md:text-2xl font-bold text-white">
            Qualifying sites: 30+ staff or steady foot traffic
          </div>
          <div className="text-sm text-zinc-300 mt-1">
            Warehouses, workshops, offices, gyms, factories, construction sites, and more.
          </div>
        </div>
        <a
          href="#book-form"
          data-testid="free-qualifying-cta"
          className="inline-flex items-center justify-center rounded-full bg-[#FE7200] px-6 py-3 text-sm font-bold uppercase tracking-wider text-black hover:bg-[#FF8C2E] transition-colors"
        >
          Book Free Site Visit
        </a>
      </div>
    </section>
  );
}

function ProcessSection() {
  const steps = [
    { n: 1, title: "Book a site visit", text: "We confirm staff numbers, access, and the best placement area." },
    { n: 2, title: "Choose your products", text: "We build your product list with snacks, drinks, and healthier options." },
    { n: 3, title: "We install", text: "Machine is delivered, tested, and ready to use immediately." },
    { n: 4, title: "We restock & service", text: "Ongoing restocking and quick maintenance response as needed." },
  ];
  return (
    <section id="how-it-works" className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-white/5" data-testid="free-process-section">
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
        How the free vending machine process works
      </h2>
      <p className="mt-3 text-zinc-300">Simple, fast, and designed to be zero hassle.</p>
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        {steps.map((s, i) => (
          <div
            key={s.n}
            data-testid={`free-step-${i}`}
            className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7 flex gap-5"
          >
            <div className="shrink-0 h-10 w-10 rounded-full bg-[#FE7200] text-black font-display font-extrabold flex items-center justify-center text-lg">
              {s.n}
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-white">{s.title}</h3>
              <p className="text-sm text-zinc-400 mt-1">{s.text}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function MachineAndCategoriesSection({ cfg }) {
  const chips = ["Water", "Soft drinks", "Energy drinks", "Chips", "Chocolate", "Protein bars", "Healthy snacks", "Food options"];
  return (
    <section className="mx-auto max-w-7xl px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-white/5 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="free-machine-categories">
      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7" data-testid="free-machine-details">
        <h3 className="font-display text-xl font-bold text-white">Machine details</h3>
        <ul className="mt-4 space-y-2 text-sm text-zinc-300">
          <li><b className="text-white">Dimensions:</b> 1830mm H · {cfg.machineWidth} · 830mm D</li>
          <li><b className="text-white">Power usage:</b> {cfg.powerCost}</li>
          <li><b className="text-white">Payments:</b> Tap &amp; Go, PayPass, cash &amp; coin</li>
          <li><b className="text-white">Stock:</b> Snacks, drinks, food + healthy options</li>
        </ul>
        <p className="mt-3 text-xs text-zinc-500">
          We'll confirm access (trolley / forklift / stairs) during the site visit.
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6 md:p-7" data-testid="free-workplace-picks">
        <h3 className="font-display text-xl font-bold text-white">Popular workplace picks</h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {chips.map((c) => (
            <span key={c} className="inline-flex items-center rounded-full border border-white/10 bg-[#0f0f10] px-3 py-1.5 text-xs text-zinc-200">
              {c}
            </span>
          ))}
        </div>
        <div className="mt-5 rounded-xl border border-[#FE7200]/40 bg-[#FE7200]/10 p-4 text-sm text-[#FFB870]">
          "No junk food" policy? Easy — we can stock water, kombucha, nut bars and more.
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  return (
    <section className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-20 md:py-24 border-t border-white/5" data-testid="free-faq-section">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight">FAQ</h2>
      <p className="mt-3 text-zinc-300">Quick answers to common questions.</p>
      <div className="mt-8">
        <Accordion items={FAQS} testIdPrefix="free-faq" defaultOpen={0} />
      </div>
    </section>
  );
}

function FormSection() {
  return (
    <section id="book-form" className="mx-auto max-w-5xl px-4 md:px-8 lg:px-12 py-10 md:py-16 border-t border-white/5" data-testid="free-form-section">
      <SiteVisitForm source="free-vending-machines" testIdPrefix="free" />
    </section>
  );
}
